import { useEffect, useRef } from 'react';
import { useKioskState } from '../../../../../context/Kiosk/kiosk';
import { CTAButton } from '../../../../../controls/ButtonBase';
import { useLocalization, useLocale } from '../../../../../context/Localization/localization';
import { AppointmentQRCode } from '../../../../../controls/AppointmentQRCode/AppointmentQRCode';
import { ScrollableBody } from '../../../../Layout/ScrollableBody';
import css from './FinishAtLocker.module.scss';
import { useSetHeaderProps, useSetKioskBannerVisibility } from '../../../../../context/Page/page';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { useIsLockerOpComplete } from '../IsLockerOpComplete';
import { Spinner } from '../../../../../controls/Spinner/Spinner';
import { useTowInProcess } from '../../../../../context/Appointment/appointment';
import { GMDialogError } from '../../../../../controls/Dialog';
import { GMSizedIcon } from '../../../../../controls/Icon';
import { IconButton } from '../../../../../controls/IconButton/IconButton';
import { GoMotoComponent } from '../../../../../API/GoMoto.types';
import { FinishAtLockerProps } from './FinishAtLocker.types';
import { LocalizationType } from '../../../../../context/Localization/localization.types';

/**
 * Displays the user's QR Code and instructions to store their key in the 365 locker to complete
 * their check-in appointment.
 *
 * @param onLockerClosed - {@link ConfirmInformationProps.onLockerClosed onLockerClosed} description.
 * @param onLockerDidntOpen - {@link ConfirmInformationProps.onLockerDidntOpen onLockerDidntOpen} description.
 * @param appointment - {@link ConfirmInformationProps.appointment appointment} description.
 */
export const FinishAtLocker: GoMotoComponent<FinishAtLockerProps> = ({
    onLockerClosed,
    onLockerDidntOpen,
    appointment,
    onLockerAssigned,
}) => {
    const Localization: LocalizationType = useLocalization();
    const { localization } = useKioskState();
    const locale = useLocale();
    const { kioskType } = useKioskState();
    const { complete, lockerNumber, kioskKey, lockerRetrieved, error, id } = useIsLockerOpComplete(appointment.keySlotNumber ?? 0);
    const setHeader = useSetHeaderProps();
    const showBanner = useSetKioskBannerVisibility();
    const navigate: NavigateFunction = useNavigate();
    const isTowIn: boolean = useTowInProcess();
    const checkInInstructions: string = kioskType === 'outdoor' ? Localization.CheckInInstructions365 : Localization.CheckInInstructionsExpress;
    const contactPreference: string = appointment.contactPreference === 'text' ? Localization.PhoneNumber.toLowerCase() : Localization.EmailAddress.toLowerCase();

    useEffect(() => {
        setHeader({
            title: Localization.FinishAtLocker,
            rightIcons: [
                <IconButton
                    key={'em-sr-close'}
                    sizedIcon={
                        <GMSizedIcon
                            iconType={'Close'}
                            iconSize={'medium'}
                            ariaLabel={Localization.CloseIcon}
                            title={Localization.ClickToCloseWindow}
                        />
                    }
                    onClick={() => navigate(-1)}
                    ariaLabel={Localization.ClickToCloseWindow}
                    id={Localization.ClickToCloseWindow}
                />,
            ],
        });
        showBanner(false);

        return (() => {
            setHeader(undefined);
            showBanner(true);
        });
    }, [setHeader, showBanner, navigate, Localization, locale]);

    const onLockerAssignedCalled = useRef(false);

    useEffect(() => {
        if (lockerRetrieved && lockerNumber > 0 && error === 0 && onLockerAssigned && !onLockerAssignedCalled.current) {
            onLockerAssignedCalled.current = true;
            onLockerAssigned();
        }
    }, [lockerRetrieved, lockerNumber, error, onLockerAssigned]);

    useEffect(() => {
        if (complete && appointment.keyStatus !== 'slot_assigned') {
            onLockerClosed(lockerNumber, kioskKey, id);
        }
    }, [complete, onLockerClosed, lockerNumber, appointment.keyStatus, kioskKey, id]);

    const onErrorClose: VoidFunction = () => {
        onLockerDidntOpen();
    };

    return (
        <ScrollableBody
            proceedButtons={
                <CTAButton
                    styleType={'secondary'}
                    onClick={onLockerDidntOpen}
                    label={Localization.LockerDidntOpen}
                    kioskOptions={{
                        inlineOptions: {
                            sizedIcon: (
                                <GMSizedIcon
                                    iconSize={'custom'}
                                    iconType={'Complete'}
                                    ariaLabel={Localization.CompleteCheckmarkIcon}
                                />
                            ),
                        },
                    }}
                    id={Localization.LockerDidntOpen}
                    ariaLabel={Localization.LockerDidntOpen}
                />
            }
        >
            {isTowIn ? (
                <>
                    <h2 className={css.title}>{Localization.InformationGathered}</h2>
                    <p>{Localization.DropOffFinishAtLocker}</p>
                </>
            ) : (
                <>
                    <h2 className={css.title}>{Localization.CheckInDetailsSaved}</h2>
                    <p>{Localization.formatString(Localization.CheckInFinishAtLocker, checkInInstructions, contactPreference)}</p>
                </>
            )}
            {localization.location ? (
                <>
                    <h2 className={css.title}>{Localization.Location}</h2>
                    <p>{localization.location}</p>
                </>
            ) : null}
            {!lockerRetrieved ? (
                <Spinner />
            ) : lockerNumber === 0 || error > 0 ? (
                <GMDialogError
                    ariaLabel={Localization.CheckInError}
                    open={true}
                    onClose={onErrorClose}
                >
                    {Localization.CheckInError}
                </GMDialogError>
            ) : (
                <AppointmentQRCode
                    mobileCheckInComplete
                    email={false}
                    lockerNumber={lockerNumber}
                />
            )}
            <p>{Localization.formatString(Localization.WhenLockerOpensDropOff, <b>{Localization.BeSureLockerCloses}</b>)}</p>
        </ScrollableBody>
    );
};
